import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from './useCurrentClient';

const STORAGE_PREFIX = 'clinance:tx';

function buildKey(userId: string, clientId: string, key: string): string {
  return `${STORAGE_PREFIX}:${userId}:${clientId}:${key}`;
}

function readFromStorage<T>(storageKey: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeToStorage(storageKey: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // ignore quota/modo privado
  }
}

/**
 * Estado que persiste em localStorage com chave por (usuário × cliente).
 * - Hidrata na mount com o valor salvo (ou `initial` se não houver).
 * - Re-hidrata quando o `clientId` muda (carrega o storage do cliente novo).
 * - Persiste a cada mudança via setter.
 *
 * Útil para filtros de listas que precisam sobreviver à navegação mas são
 * específicos do contexto do cliente atual.
 */
export function usePersistedClientState<T>(
  key: string,
  initial: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const { user } = useAuth();
  const { clientId } = useCurrentClient();
  const userId = user?.id ?? 'anon';
  const activeClientId = clientId ?? '_none';
  const storageKey = buildKey(userId, activeClientId, key);

  const [value, setValue] = useState<T>(() => readFromStorage(storageKey, initial));
  const lastStorageKey = useRef(storageKey);

  /* eslint-disable react-hooks/set-state-in-effect */
  // Quando a chave muda (trocou cliente ou usuário), re-hidrata.
  useEffect(() => {
    if (lastStorageKey.current === storageKey) return;
    lastStorageKey.current = storageKey;
    setValue(readFromStorage(storageKey, initial));
  }, [storageKey, initial]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Persiste em qualquer set.
  useEffect(() => {
    writeToStorage(storageKey, value);
  }, [storageKey, value]);

  return [value, setValue];
}
